import { Subscription, fromEvent } from 'rxjs';
import { ElementRef } from '@angular/core';
import { WizEvent } from './wiz-event';
import { WizMathUtils } from '@wizeoo/akm-core-lib';

// gestionnaire d'événements
// pour prise en compte du bubbling
// et du typage direct (in / out)
// statique
export class WizEventManager {

  constructor( ) {
  }

  // injection du Wizroot.elementRef (par le Wizroot sur son init)
  static root: ElementRef = null; 

  static eventWatcherId: string = 'eventWatcherId';

  /** pile de listeners, pour gestion / suppression
   * inscription sur addListener, suppression sur removeListener
   * clé = source.eventWatcherId | elementRef.native.id | eventType = [l'élément de dom , le listener]
   */
  private static listeners: Map<string, Subscription> = new Map();

  /** mise en place d'un écouteur
   * @param source : le component à l'origine de la pose de l'écouteur (possède la méthode)
   * @param elementRef : l'objet "physique" pour collecte de son ancre html (.nativeElement).
   *                    Peut être un ElementRef directement ou bien un objet qui possède une propriété element ref
   * @param eventType : le type d'événement, une constante de WizEvent.xxx ou d'un héritage de cette classe
   * @param method : la fonction à appeler pour le traitement sous la forme (item : WizEvent) => this.mafunc(item)
   * @param options passer le mode capture
   */
  public static addListener(source: any,
                            pElemRef: ElementRef | { elementRef: ElementRef },
                            eventType: string,
                            listener: Function,
                            options?: EventListenerOptions) {

    listener = listener.bind(source); // forcer le binding pour ne pas perdre le "this" de l'objet d'origine
    // get the right element ref
    const elemRef: ElementRef = pElemRef instanceof ElementRef ? pElemRef : pElemRef.elementRef;
    // création du listener
    /*const meListener: EventListener = (item: CustomEvent) => method(item.detail as WizEvent);

    // pose de l'écouteur
    elemRef.nativeElement.addEventListener(eventType, meListener);*/

    // The observable to that is used as a proxy to control the event before calling the listener
    const subscription = fromEvent(elemRef.nativeElement, eventType, options)
      .subscribe((sourceEvent: CustomEvent) => {
      const event: any = sourceEvent.detail;

      // default behavior has been prevented
      if (sourceEvent.defaultPrevented) {
        return;
      }

      // We check if the detail is a WizEvent to reverse the composition
      if (event instanceof WizEvent) {
        event.sourceEvent = sourceEvent;
        listener(event);
      } else {
        // Not a WizEvent, we move on and call the listener
        listener(sourceEvent);
      }
    });

    // enregistrement
    if (elemRef.nativeElement.id === '') { // si pas d'ID sur le composant cible, on en pose un
      elemRef.nativeElement.id = WizMathUtils.createUUID();
    }
    if (this.eventWatcherId in source) {} else { // si l'origine de l'écouteur ne possède pas d'ID de watcher, pose
      source[this.eventWatcherId] = WizMathUtils.createUUID();
    }

    // build the key
    const key: string = source[this.eventWatcherId] + '|' + elemRef.nativeElement.id + '|' + eventType;

    // listener already exists we warn the user
    if (this.hasEventListener(source, pElemRef, eventType)) {
      // console.warn('A listener already exists for the given element');
    }

    // console.log ('meads', key);
    this.listeners.set(key, subscription);

  }

  /** suppression d'un écouteur
   * @param source : le component à l'origine de la pose de l'écouteur
   * @param elementRef : l'objet "physique" pour collecte de son ancre html (.nativeElement)
   * @param eventType : le type d'événement, une constante de WizEvent.xxx ou d'un héritage de cette classe
   */
  public static removeListener(source: object, pElemRef: ElementRef | { elementRef: ElementRef }, eventType: string ) {
    const elemRef: ElementRef = pElemRef instanceof ElementRef ? pElemRef : pElemRef.elementRef;

    if (elemRef.nativeElement.id !== '' && this.eventWatcherId in source && eventType) { // les 3 éléments existent
      const key: string = source[this.eventWatcherId] + '|' + elemRef.nativeElement.id + '|' + eventType;

      // checks if the listener exists
      if (!this.listeners.has(key)) {
        // console.warn(`No listener was found for key ${key} and eventType ${eventType}`);
        return;
      }

      const meToDie: Subscription  = this.listeners.get(key);
      meToDie.unsubscribe();
      this.listeners.delete(key);
    }
  }

  /**
   * Dispatch of a bubbling event
   * @param pElementRef Ref containing the DOM element that will dipatch the bubbling event
   * @param pEvent Event to be encapsulated inside the bubbling event
   */
  public static dispatchEvent(pElemRef: ElementRef | { elementRef: ElementRef }, pEvent: WizEvent) {
    const elemRef: ElementRef = pElemRef instanceof ElementRef ? pElemRef : pElemRef.elementRef;

    if (elemRef && elemRef.nativeElement && pEvent) {
      elemRef.nativeElement.dispatchEvent(new CustomEvent<WizEvent>( pEvent.type, { bubbles: true, cancelable: true, detail: pEvent }));
    }
  }

  /**
   * Controls if an event has already been added for the given elements
   * @param source : le component à l'origine de la pose de l'écouteur
   * @param elementRef : l'objet "physique" pour collecte de son ancre html (.nativeElement)
   * @param eventType : le type d'événement, une constante de WizEvent.xxx ou d'un héritage de cette classe
   */
  public static hasEventListener(source: object, pElemRef: ElementRef | { elementRef: ElementRef }, eventType: string): boolean {
    const elemRef: ElementRef = pElemRef instanceof ElementRef ? pElemRef : pElemRef.elementRef;

    if (elemRef.nativeElement.id !== '' && this.eventWatcherId in source && eventType) { // les 3 éléments existent
      const key: string = source[this.eventWatcherId] + '|' + elemRef.nativeElement.id + '|' + eventType;
      return this.listeners.has(key);
    }
    return false;
  }

  /**
   * dispatch d'un event sur Wizroot pour tout le monde
   * @param e WizEvent
   */
  public static dispatchRootEvent(e: WizEvent): void {
    if (this.root) { // seulement si root est dispo
      this.dispatchEvent(this.root, e);
    }
  }

}
